import React from 'react';
import { sortBy } from 'lodash';
// Api
import { GetCampaignsV2_getCampaigns_entities_activities_SocialActivity } from 'api/campaignV2/types/GetCampaignsV2';
// Helpers
import {
  getTaskTypeText,
  getContentTypeText,
} from 'helpers/socialMediaContentTypes';
import { parseForInnerHTMLWithBreaks } from 'helpers/textEditor';
import {
  getPostDueDateLabel,
  getContentDueDateLabel,
} from 'helpers/timelineDeliverables';
// Styles
import styles from '../CampaignActivityData.module.scss';

type SocialActivityProps = {
  socialActivity: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity;
  isV2Campaign: boolean;
  isV3Campaign?: boolean;
};

const SocialActivityV1 = ({ socialActivity }: Partial<SocialActivityProps>) => {
  return (
    <>
      {socialActivity?.mediaType?.length && (
        <div>
          <p className={styles.label}>Type of media</p>
          <p className={styles.value}>
            {socialActivity.mediaType
              ?.map((item) => item?.toLowerCase())
              .join(', ')}
          </p>
        </div>
      )}

      {!!socialActivity?.tasks?.length && (
        <div>
          <p className={styles.label}>Post type</p>
          <p className={styles.value}>
            {socialActivity?.tasks
              .map((task) => getContentTypeText(task))
              .join(', ')}
          </p>
        </div>
      )}

      {socialActivity && (
        <div>
          <p className={styles.label}>Suggested caption</p>
          <p
            className={styles.value}
            dangerouslySetInnerHTML={{
              __html: parseForInnerHTMLWithBreaks(
                socialActivity.suggestedCaption ?? 'n/a'
              ),
            }}
          />
        </div>
      )}
    </>
  );
};

const SocialActivityV2 = ({ socialActivity }: Partial<SocialActivityProps>) => {
  return (
    <>
      {!!socialActivity?.tasks?.length && (
        <div>
          <p className={styles.label}>Deliverables Checklist</p>
          {sortBy(socialActivity?.tasks, 'orderIdx').map((task, index) => {
            return (
              <>
                <p key={task.title + '-' + index} className={styles.label}>
                  {`Deliverable ${index + 1}:`} {task.title}
                </p>
                <p className={styles.value}>Type: {getContentTypeText(task)}</p>
                <p className={styles.value}>Description: {task.description}</p>
              </>
            );
          })}
        </div>
      )}
    </>
  );
};

const SocialActivityV3 = ({ socialActivity }: Partial<SocialActivityProps>) => {
  return (
    <>
      {!!socialActivity?.tasksV2?.length && (
        <div>
          <p className={styles.label}>Timeline Deliverables</p>
          {sortBy(socialActivity?.tasksV2, 'orderIdx').map((task, index) => {
            const contentItems = task.contentItems || [];
            const hasContentItems = contentItems.length > 0;

            return (
              <div
                key={task.id || task.title + '-' + index}
                className={styles.deliverableContainer}
              >
                <p className={styles.label}>
                  {`Deliverable ${index + 1}:`} {task.title}
                </p>

                <div className={styles.deliverableDetails}>
                  <p className={styles.value}>
                    <strong>Type:</strong> {getTaskTypeText(task)}
                  </p>

                  {task.description && (
                    <p className={styles.value}>
                      <strong>Description:</strong> {task.description}
                    </p>
                  )}

                  {task.suggestedCaption && (
                    <div className={styles.suggestedCaptionContainer}>
                      <p className={styles.label}>Suggested Caption</p>
                      <p
                        className={styles.value}
                        dangerouslySetInnerHTML={{
                          __html: parseForInnerHTMLWithBreaks(
                            task.suggestedCaption
                          ),
                        }}
                      />
                    </div>
                  )}

                  <p className={styles.value}>
                    <strong>Content Due Date:</strong>{' '}
                    {getContentDueDateLabel(task)}
                  </p>

                  <p className={styles.value}>
                    <strong>Post Due Date:</strong> {getPostDueDateLabel(task)}
                  </p>

                  {hasContentItems && (
                    <div className={styles.contentItemsContainer}>
                      <p className={styles.label}>Content Pieces</p>
                      {contentItems.map((contentItem, contentIndex) => (
                        <div
                          key={contentItem.id || contentIndex}
                          className={styles.contentItem}
                        >
                          <p className={styles.value}>
                            <strong>Content {contentIndex + 1}:</strong>{' '}
                            {contentItem.title}
                          </p>
                          {contentItem.description && (
                            <p className={styles.value}>
                              <strong>Description:</strong>{' '}
                              {contentItem.description}
                            </p>
                          )}
                          {contentItem.__typename === 'CampaignContentTask' &&
                            contentItem.mediaType && (
                              <p className={styles.value}>
                                <strong>Media Type:</strong>{' '}
                                {contentItem.mediaType.toLowerCase()}
                              </p>
                            )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const SocialActivity = ({
  socialActivity,
  isV2Campaign,
  isV3Campaign = false,
}: SocialActivityProps): JSX.Element => {
  return (
    <>
      {isV3Campaign ? (
        <SocialActivityV3 socialActivity={socialActivity} />
      ) : isV2Campaign ? (
        <SocialActivityV2 socialActivity={socialActivity} />
      ) : (
        <SocialActivityV1 socialActivity={socialActivity} />
      )}
    </>
  );
};

export default SocialActivity;
